import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

interface AllowedSender {
  email: string;
  name: string;
  type: 'email' | 'domain';
  addedDate?: string;
}

interface EmailFiltersData {
  enabled: boolean;
  allowedSenders: AllowedSender[];
  defaultAction: 'allow' | 'block';
}

interface FilterLog {
  _id: string;
  senderEmail: string;
  senderName: string;
  subject: string;
  action: 'allowed' | 'blocked';
  reason: string;
  processedAt: string;
}

const EmailFilters: React.FC = () => {
  const [filters, setFilters] = useState<EmailFiltersData>({
    enabled: false,
    allowedSenders: [],
    defaultAction: 'allow'
  });
  const [newSender, setNewSender] = useState({
    email: '',
    name: '',
    type: 'email' as 'email' | 'domain'
  });
  const [filterLogs, setFilterLogs] = useState<FilterLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadFilters();
    loadFilterLogs();
  }, []);

  const loadFilters = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/email-filters`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data) {
        setFilters(response.data.emailFilters || {
          enabled: false,
          allowedSenders: [],
          defaultAction: 'allow'
        });
      }
    } catch (error) {
      console.error('Error loading filters:', error);
    }
  };

  const loadFilterLogs = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/email-filter-logs?limit=20`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data) {
        setFilterLogs(response.data.logs || []);
      }
    } catch (error) {
      console.error('Error loading filter logs:', error);
    }
  };

  const saveFilters = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await axios.put(
        `${API_BASE_URL}/users/email-filters`,
        { emailFilters: filters },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.status === 200) {
        setSuccess('Email filters saved successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save filters');
    }
    setLoading(false);
  };

  const addSender = () => {
    if (!newSender.email.trim()) {
      setError('Email address is required');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const domainRegex = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    if (newSender.type === 'email' && !emailRegex.test(newSender.email)) {
      setError('Invalid email format');
      return;
    }
    
    if (newSender.type === 'domain') {
      const domain = newSender.email.replace('@', '');
      if (!domainRegex.test(domain)) {
        setError('Invalid domain format');
        return;
      }
    }

    // Check for duplicates
    const exists = filters.allowedSenders.some(sender => 
      sender.email.toLowerCase() === newSender.email.toLowerCase() && 
      sender.type === newSender.type
    );

    if (exists) {
      setError('This sender is already in your list');
      return;
    }

    setFilters({
      ...filters,
      allowedSenders: [...filters.allowedSenders, {
        ...newSender,
        email: newSender.email.toLowerCase(),
        addedDate: new Date().toISOString()
      }]
    });

    setNewSender({ email: '', name: '', type: 'email' });
    setError('');
  };

  const removeSender = (index: number) => {
    const updatedSenders = filters.allowedSenders.filter((_, i) => i !== index);
    setFilters({
      ...filters,
      allowedSenders: updatedSenders
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Email Sender Filters</h2>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {/* Enable/Disable Toggle */}
      <div className="mb-6">
        <label className="flex items-center">
          <input
            type="checkbox"
            className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            checked={filters.enabled}
            onChange={(e) => setFilters({
              ...filters,
              enabled: e.target.checked
            })}
          />
          <span className="ml-2 text-sm font-medium text-gray-700">
            Enable email sender filtering
          </span>
        </label>
        <p className="mt-1 text-sm text-gray-500">
          When enabled, only emails from approved senders will create todos
        </p>
      </div>

      {/* Default Action */}
      {filters.enabled && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Default action for unknown senders
          </label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={filters.defaultAction}
            onChange={(e) => setFilters({
              ...filters,
              defaultAction: e.target.value as 'allow' | 'block'
            })}
          >
            <option value="allow">Allow (create todo)</option>
            <option value="block">Block (reject email)</option>
          </select>
        </div>
      )}

      {/* Add New Sender */}
      {filters.enabled && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Add Allowed Sender</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={newSender.type}
                onChange={(e) => setNewSender({
                  ...newSender,
                  type: e.target.value as 'email' | 'domain',
                  email: ''
                })}
              >
                <option value="email">Specific Email</option>
                <option value="domain">Entire Domain</option>
              </select>
            </div>
            
            <div>
              <input
                type="text"
                placeholder={newSender.type === 'email' ? 'user@example.com' : 'company.com'}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={newSender.email}
                onChange={(e) => setNewSender({
                  ...newSender,
                  email: e.target.value
                })}
              />
            </div>
            
            <div>
              <input
                type="text"
                placeholder="Display name (optional)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={newSender.name}
                onChange={(e) => setNewSender({
                  ...newSender,
                  name: e.target.value
                })}
              />
            </div>
            
            <div>
              <button
                type="button"
                onClick={addSender}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Allowed Senders List */}
      {filters.enabled && filters.allowedSenders.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Allowed Senders</h3>
          <div className="space-y-2">
            {filters.allowedSenders.map((sender, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">
                      {sender.type === 'domain' ? `@${sender.email}` : sender.email}
                    </span>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      {sender.type}
                    </span>
                  </div>
                  {sender.name && (
                    <p className="text-sm text-gray-600">{sender.name}</p>
                  )}
                  {sender.addedDate && (
                    <p className="text-xs text-gray-500">
                      Added: {formatDate(sender.addedDate)}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => removeSender(index)}
                  className="text-red-600 hover:text-red-800 p-1"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveFilters}
          disabled={loading}
          className="bg-green-600 text-white py-2 px-6 rounded-md hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Saving...' : 'Save Filters'}
        </button>
      </div>

      {/* Filter Activity Log */}
      {filters.enabled && filterLogs.length > 0 && (
        <div className="mt-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Filter Activity</h3>
          <div className="space-y-3">
            {filterLogs.map((log) => (
              <div key={log._id} className={`p-3 rounded-lg ${
                log.action === 'allowed' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs px-2 py-1 rounded ${
                        log.action === 'allowed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {log.action.toUpperCase()}
                      </span>
                      <span className="font-medium">{log.senderEmail}</span>
                    </div>
                    <p className="text-sm text-gray-700 mt-1">{log.subject}</p>
                    <p className="text-xs text-gray-500">{log.reason}</p>
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatDate(log.processedAt)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailFilters;